import React, { useRef, useImperativeHandle, forwardRef, useMemo } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './RichEditor.css';

// API Configuration
import { fetchWithAuth } from '../../../api/apiClient';
import { BASE_URL } from '../../../api/inquiries';

// Convert Quill to use inline styles instead of classes for better global rendering
const SizeStyle = Quill.import('attributors/style/size');
SizeStyle.whitelist = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '36px'];
Quill.register(SizeStyle, true);

const AlignStyle = Quill.import('attributors/style/align');
Quill.register(AlignStyle, true);

const MAX_FILE_SIZE = 7 * 1024 * 1024; // 7MB

const RichEditor = forwardRef(({
    initialValue = '',
    height = '400px',
    onChange,
    placeholder = '내용을 입력하세요...'
}, ref) => {
    const quillRef = useRef(null);

    // Provide generic methods to any Parent Component
    useImperativeHandle(ref, () => ({
        getHTML: () => {
             // React-quill's value is HTML
             const editor = quillRef.current?.getEditor();
             return editor?.root.innerHTML || '';
        },
        getMarkdown: () => {
             const editor = quillRef.current?.getEditor();
             return editor?.root.innerHTML || ''; // Quill primarily outputs HTML, markdown export requires third-party parsers
        },
        setHTML: (html) => {
             const editor = quillRef.current?.getEditor();
             if (editor) {
                 editor.clipboard.dangerouslyPasteHTML(html || '');
             }
        },
        setMarkdown: (md) => {
             const editor = quillRef.current?.getEditor();
             if (editor) {
                 editor.clipboard.dangerouslyPasteHTML(md || ''); 
             }
        }
    }));

    // Custom Image Handler
    const imageHandler = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;

            if (file.size > MAX_FILE_SIZE) {
                alert(`이미지는 7MB 이하만 첨부할 수 있습니다.\n현재 파일 크기: ${(file.size / 1024 / 1024).toFixed(1)}MB`);
                return;
            }

            try {
                const formData = new FormData();
                formData.append('file', file);
                
                const response = await fetchWithAuth(`${BASE_URL}/api/upload?folder=editor`, {
                    method: 'POST',
                    body: formData
                });

                if (!response.ok) throw new Error('Upload failed');

                const data = await response.json();
                const imageUrl = data.url || data.path;

                const editor = quillRef.current.getEditor();
                const range = editor.getSelection();
                editor.insertEmbed(range.index, 'image', imageUrl);
            } catch (error) {
                console.error('Image upload failed:', error);
                alert('이미지 업로드에 실패했습니다.');
            }
        };
    };

    // Memoize modules to prevent re-renders disconnecting the editor focus (very important for React Quill)
    const modules = useMemo(() => ({
        toolbar: {
            container: [
                [{ 'size': SizeStyle.whitelist }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                [{ 'align': [] }],
                [{ 'color': [] }, { 'background': [] }],
                ['link', 'image', 'video'],
                ['clean']
            ],
            handlers: {
                image: imageHandler
            }
        }
    }), []);

    return (
        <div className="rich-editor-container" style={{ width: '100%', minHeight: height }}>
            <ReactQuill 
                ref={quillRef}
                theme="snow"
                defaultValue={initialValue}
                onChange={onChange}
                modules={modules}
                placeholder={placeholder}
                style={{ height: `calc(${height} - 42px)`, paddingBottom: '42px' }}
            />
        </div>
    );
});

RichEditor.displayName = 'RichEditor';
export default RichEditor;
